/**
 * BlockedProposalCard — TASK-681 deep review.
 *
 * The terminal state for a proposal the house rails refused.
 *
 * Before this, a blocked proposal produced no UI at all: the tool returned the refusal to
 * the model and the generator dropped the failed result. If the model had already spent its
 * wall-clock budget, the RM's turn ended on assistant prose — no proposal, no reason, no way
 * forward. "Let me create a proposal" is not a terminal state.
 *
 * This renders without needing another LLM turn: which rail failed, the figures from the
 * rails that did run (via the shared VerifiedConstraints renderer, so a breach looks the same
 * here as in the composer), and two recovery actions.
 */

import React from 'react';
import { ShieldAlert, RefreshCw, SlidersHorizontal } from 'lucide-react';
import { VerifiedConstraints } from '../../portfolio/VerifiedConstraints';
import type { VerifiedConstraint, RiskDelta } from '../../portfolio/VerifiedConstraints';
import type { ProposalActionItem } from '../../../types/prepChat.types';

/** BUY-rated instruments in the slot a refused instrument was aiming at (TASK-686 review). */
export interface SuggestedAlternativeGroup {
  for_symbol: string;
  sub_asset_class: string;
  alternatives: { isin: string; issuer: string; name?: string; cio_view?: string }[];
}

interface BlockedProposalCardProps {
  proposal: ProposalActionItem;
  /** Ask the assistant to revise — prefilled prompt naming the failed rails. */
  onRevise?: (prompt: string) => void;
  /** Open the composer empty so the advisor can build the trades by hand. */
  onOpenComposer?: () => void;
}

export const BlockedProposalCard: React.FC<BlockedProposalCardProps> = ({
  proposal,
  onRevise,
  onOpenComposer,
}) => {
  const data = (proposal.data ?? {}) as {
    title?: string;
    block_reasons?: string[];
    verified_constraints?: VerifiedConstraint[];
    risk_deltas?: RiskDelta[];
    // TASK-686 review: what the RM CAN buy in the slot the refused instrument wanted.
    suggested_alternatives?: SuggestedAlternativeGroup[];
  };

  const reasons = data.block_reasons?.length
    ? data.block_reasons
    : ['The proposal could not be validated.'];

  const groups = data.suggested_alternatives ?? [];
  // Two refused instruments in the same slot get the same substitutes, and printing the list
  // twice reads as two different answers. One row per slot, naming everything it replaces.
  const bySlot = groups.reduce<Record<string, { symbols: string[]; alternatives: SuggestedAlternativeGroup['alternatives'] }>>(
    (acc, g) => {
      const slot = (acc[g.sub_asset_class] ??= { symbols: [], alternatives: g.alternatives });
      slot.symbols.push(g.for_symbol);
      return acc;
    },
    {},
  );

  const handleRevise = () => {
    // Naming the approved instruments in the retry prompt is the whole point of fetching
    // them: without it the model has no view of the CIO universe and re-guesses an ISIN.
    const namedAlternatives = Object.entries(bySlot)
      .map(
        ([slot, { alternatives }]) =>
          `${slot}: ${alternatives.map((a) => `${a.isin} (${a.issuer})`).join(', ')}`,
      )
      .join('. ');

    onRevise?.(
      `Revise the proposal so it passes the house rails. It was blocked because: ${reasons.join(
        '; ',
      )}.` +
        (namedAlternatives
          ? ` Use one of these CIO BUY-rated instruments instead — ${namedAlternatives}.`
          : '') +
        ` Avoid the client's excluded value tags, and size the trade to stay inside the drift band.`,
    );
  };

  return (
    <div
      data-testid="blocked-proposal-card"
      className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3"
    >
      <div className="flex items-start gap-2">
        <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-red-800">
            Proposal blocked — not executable
          </p>
          {data.title && (
            <p className="truncate text-xs text-red-700/80">{data.title}</p>
          )}

          <ul className="mt-2 space-y-1">
            {reasons.map((reason, idx) => (
              <li key={idx} className="text-xs leading-snug text-red-800">
                • {reason}
              </li>
            ))}
          </ul>

          {/* The rails that DID run, with their figures — same renderer as the composer. */}
          {!!data.verified_constraints?.length && (
            <div className="mt-3">
              <VerifiedConstraints
                constraints={data.verified_constraints}
                riskDeltas={data.risk_deltas}
              />
            </div>
          )}

          {/* A refusal the advisor can act on: the approved names in the same slot. Rendered
              only when the house view actually returned some — an empty list means we do not
              know what fits, and inventing a substitute here would be the exact failure the
              CIO rail exists to stop. */}
          {groups.length > 0 && (
            <div
              data-testid="suggested-alternatives"
              className="mt-3 rounded-md border border-red-200 bg-white p-2"
            >
              <p className="text-xs font-semibold text-gray-700">
                CIO BUY-rated in the same slot
              </p>
              {Object.entries(bySlot).map(([slot, { symbols, alternatives }]) => (
                <div key={slot} className="mt-1.5">
                  <p className="text-[11px] uppercase tracking-wide text-gray-500">
                    instead of {symbols.join(', ')} · {slot}
                  </p>
                  <ul className="mt-0.5 space-y-0.5">
                    {alternatives.map((alt) => (
                      <li key={alt.isin} className="text-xs text-gray-800">
                        <span className="font-mono">{alt.isin}</span> — {alt.issuer}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          <div className="mt-3 flex flex-wrap gap-2">
            {onRevise && (
              <button
                type="button"
                onClick={handleRevise}
                className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-red-700"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Revise proposal
              </button>
            )}
            {onOpenComposer && (
              <button
                type="button"
                onClick={onOpenComposer}
                className="inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-white px-2.5 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50"
              >
                <SlidersHorizontal className="h-3.5 w-3.5" />
                Open composer manually
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlockedProposalCard;
